import { useState, useEffect } from 'react';
import PolicyViolationsService from '../../../../../../services/PolicyViolations';
import PerformanceImprovementService from '../../../../../../services/PerformanceImprovement';
import { INITIAL_FORM_DATA } from './formConstants';
import { UserWithEmployee } from '../../UserSelectionModal';

interface FormData {
  name: string;
  date: string;
  position: string;
  department: string;
  supervisor: string;
  dateOfHire: string;
  policyViolationIds: string[];
  reasonForPerformanceImprovementPlan: string;
  expectationsAndGoals: string;
  timelineAndDeadline: string;
  resourcesAndSupport: string;
  privateNotes: string;
}

interface FormErrors {
  name?: string;
  date?: string;
  policyViolationIds?: string;
  reasonForPerformanceImprovementPlan?: string;
  expectationsAndGoals?: string;
  timelineAndDeadline?: string;
  resourcesAndSupport?: string;
}

interface PolicyViolation {
  id: string;
  name: string;
}

interface PriorDiscussion {
  date_of_incident: string;
  type: string;
  policy_violated: string;
  action_taken?: string;
}

export const useImprovementForm = (
  isOpen: boolean,
  token: string,
  currentUser?: UserWithEmployee,
  submittedForms?: Map<string, FormData>,
  editMode: boolean = false,
  performanceImprovementId?: string,
) => {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState<FormErrors>({});
  const [policyViolations, setPolicyViolations] = useState<PolicyViolation[]>([]);
  const [priorDiscussions, setPriorDiscussions] = useState<PriorDiscussion[]>([]);

  const loadPolicyViolations = async () => {
    try {
      const service = PolicyViolationsService(token);
      const response = await service.list();
      setPolicyViolations(response.data || []);
    } catch (error) {
      console.error('Error loading policy violations:', error);
      setPolicyViolations([]);
    }
  };

  const loadPriorDiscussions = async (userId: string) => {
    try {
      const service = PerformanceImprovementService(token);
      const response = await service.getPriorDiscussions(userId);
      setPriorDiscussions(response.data || []);
    } catch (error) {
      console.error('Error loading prior discussions:', error);
      setPriorDiscussions([]);
    }
  };

  const loadPerformanceImprovementData = async (id: string) => {
    try {
      const service = PerformanceImprovementService(token);
      const response = await service.show(id);
      const performanceImprovement = response.data;

      if (performanceImprovement) {
        let policyViolationIds: string[] = [];

        if (performanceImprovement.policyViolationIds && Array.isArray(performanceImprovement.policyViolationIds)) {
          policyViolationIds = performanceImprovement.policyViolationIds;
        } else if (performanceImprovement.policyViolations && Array.isArray(performanceImprovement.policyViolations)) {
          policyViolationIds = performanceImprovement.policyViolations.map((pv: any) => pv.id || pv);
        } else if (performanceImprovement.policyViolationId) {
          policyViolationIds = [performanceImprovement.policyViolationId];
        } else if (performanceImprovement.policyViolation?.id) {
          policyViolationIds = [performanceImprovement.policyViolation.id];
        }

        setFormData({
          name: performanceImprovement.name || '',
          date: performanceImprovement.date ? new Date(performanceImprovement.date).toISOString().split('T')[0] : '',
          position: performanceImprovement.position || '',
          department: performanceImprovement.department || '',
          supervisor: performanceImprovement.supervisor || '',
          dateOfHire: performanceImprovement.dateOfHire
            ? new Date(performanceImprovement.dateOfHire).toISOString().split('T')[0]
            : '',
          policyViolationIds,
          reasonForPerformanceImprovementPlan: performanceImprovement.reasonForPerformanceImprovementPlan || '',
          expectationsAndGoals: performanceImprovement.expectationsAndGoals || '',
          timelineAndDeadline: performanceImprovement.timelineAndDeadline || '',
          resourcesAndSupport: performanceImprovement.resourcesAndSupport || '',
          privateNotes: performanceImprovement.privateNotes || '',
        });

        if (performanceImprovement.userId) {
          loadPriorDiscussions(performanceImprovement.userId);
        }
      }
    } catch (error) {
      console.error('Error loading performance improvement data:', error);
    }
  };

  useEffect(() => {
    if (isOpen && token) {
      loadPolicyViolations();
    }
  }, [isOpen, token]);

  useEffect(() => {
    if (isOpen && editMode && performanceImprovementId && token) {
      loadPerformanceImprovementData(performanceImprovementId);
    }
  }, [isOpen, editMode, performanceImprovementId, token]);

  useEffect(() => {
    if (isOpen && !editMode && currentUser) {
      const savedForm = submittedForms?.get(currentUser.id);
      if (savedForm) {
        setFormData(savedForm);
      } else {
        const today = new Date().toISOString().split('T')[0];
        const lastSavedForm =
          submittedForms && submittedForms.size > 0 ? Array.from(submittedForms.values()).at(-1) : undefined;
        setFormData({
          name: `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim(),
          date: today,
          position: currentUser.employee?.title?.name || '',
          department: currentUser.employee?.department?.name || '',
          supervisor: currentUser.employee?.supervisor?.name || '',
          dateOfHire: currentUser.employee?.startDate || '',
          policyViolationIds: lastSavedForm?.policyViolationIds ?? [],
          reasonForPerformanceImprovementPlan: lastSavedForm?.reasonForPerformanceImprovementPlan ?? '',
          expectationsAndGoals: lastSavedForm?.expectationsAndGoals ?? '',
          timelineAndDeadline: lastSavedForm?.timelineAndDeadline ?? '',
          resourcesAndSupport: lastSavedForm?.resourcesAndSupport ?? '',
          privateNotes: lastSavedForm?.privateNotes ?? '',
        });
      }
      setErrors({});
      loadPriorDiscussions(currentUser.id);
    }
  }, [isOpen, editMode, currentUser]);

  useEffect(() => {
    if (!isOpen) {
      setFormData(INITIAL_FORM_DATA);
      setErrors({});
      setPriorDiscussions([]);
    }
  }, [isOpen]);

  const handleInputChange = (field: keyof FormData, value: string | string[]) => {
    setFormData((prev: FormData) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev: FormErrors) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    const today = new Date().toISOString().split('T')[0];

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.date) {
      newErrors.date = 'Date is required';
    } else if (formData.date > today) {
      newErrors.date = 'Date cannot be in the future';
    }
    if (formData.policyViolationIds.length === 0) {
      newErrors.policyViolationIds = 'At least one Policy Violation is required';
    }
    if (!formData.reasonForPerformanceImprovementPlan.trim()) {
      newErrors.reasonForPerformanceImprovementPlan = 'Reason for Performance Improvement Plan is required';
    }
    if (!formData.expectationsAndGoals.trim()) {
      newErrors.expectationsAndGoals = 'Expectations & Goals is required';
    }
    if (!formData.timelineAndDeadline.trim()) {
      newErrors.timelineAndDeadline = 'Timeline and/or Deadline is required';
    }
    if (!formData.resourcesAndSupport.trim()) {
      newErrors.resourcesAndSupport = 'Resources and Support is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return {
    formData,
    errors,
    policyViolations,
    priorDiscussions,
    handleInputChange,
    validateForm,
    setFormData,
  };
};
